// ESPEJO AUTOMÁTICO — el canónico es supabase/functions/_shared/laTarjetaDelRastreo.ts.
// No se edita a mano: `npm run espejos` lo regenera y `npm run espejos:gate` falla si quedó atrás.

/**
 * La tarjeta del rastreo de Shalom como imagen: lo que el panel y el servidor tienen que decir IGUAL (28-oct-2026).
 *
 * La tarjeta (`ShalomTrackingCard`) se manda como imagen por dos caminos: el operador desde Chats o Entregas (la
 * dibuja su navegador, `rastreoShalomAImagen.ts`) y los avisos automáticos al grupo del drop, que salen del servidor
 * (la dibuja el fotógrafo, `services/fotografo`, abriendo la página `/foto/rastreo-shalom` del panel). Los dos guardan
 * la imagen en el envío (`order_shipments.rastreo_imagen_url` + `rastreo_imagen_version`) y la reusan mientras siga
 * siendo verdad. Para eso tienen que estar de acuerdo en dos cosas, y viven acá para que no haya dos versiones:
 *
 *   1. LA VERSIÓN: con qué estado y con qué diseño se dibujó. Si el servidor la calculara distinto que el panel,
 *      cada uno rehacería la imagen del otro, o peor: reusaría una que ya no es verdad.
 *   2. LA PÁGINA: dónde está, cómo se le pide que pinte y qué se fotografía. El fotógrafo es genérico; el contrato
 *      se lo manda el servidor en cada pedido, y la página lo cumple.
 *
 * 🪤 ESTE ARCHIVO NO IMPORTA NADA: el panel tiene su espejo (`npm run espejos`, `src/lib/operations/`).
 */

/**
 * Qué versión del DISEÑO de la tarjeta se dibujó.
 *
 * 🚨 SIN ESTO, CAMBIAR LA TARJETA NO CAMBIA LO QUE RECIBE EL CLIENTE. La imagen se guarda por envío
 * y sólo se rehace cuando cambia el estado del rastreo; un envío que ya tiene su foto seguiría
 * mandando la de antes para siempre, aunque el diseño haya cambiado hace semanas.
 *
 * Se sube cuando cambia lo que la tarjeta MUESTRA. Ahora: al sacarle al cliente la fila de
 * remitente, contenido, destinatario y pago —«al decir "pago no cancelado" puede hacer desconfiar
 * al cliente», el founder—, todas las imágenes dibujadas antes quedan viejas por definición.
 */
export const FORMATO_DE_LA_TARJETA = 'v2-sin-fila-interna';

/**
 * Con qué estado del rastreo se dibujó la imagen.
 *
 * 🪤 SE USA EL ESTADO, NO LA FECHA DE CONSULTA. Rastrear cada cinco minutos cambia
 * `last_tracked_at` sin que cambie NADA de lo que se ve: usar la fecha regeneraría la imagen todo
 * el día para producir bytes idénticos. Lo que se dibuja es el estado, así que eso es la versión.
 */
export function versionDelRastreo(rastreo: unknown): string {
  const d = rastreo as { statuses?: { message?: string }; search?: { data?: Record<string, unknown> } } | null;
  const estado = d?.statuses?.message ?? (d?.search?.data?.estado as string | undefined) ?? '';
  return `${String(estado).trim().toLowerCase() || 'sin-estado'}|${FORMATO_DE_LA_TARJETA}`;
}

/** ¿Este rastreo tiene la forma de la tarjeta de Shalom (`{search, statuses}`)? Olva y los demás no la tienen. */
export function esRastreoDeShalom(rastreo: unknown): boolean {
  if (!rastreo || typeof rastreo !== 'object') return false;
  const d = rastreo as { search?: { data?: unknown }; statuses?: unknown };
  return !!(d.search?.data && typeof d.search.data === 'object') || !!(d.statuses && typeof d.statuses === 'object');
}

/**
 * La página del panel que pinta la tarjeta para el fotógrafo.
 *
 *   · `ruta`: la página, pública y sin datos propios: pinta sólo lo que se le pasa.
 *   · `pintar`: la función que la página deja en `window`; recibe `(rastreo, opciones)` y resuelve cuando la tarjeta
 *     ya está pintada (React pinta asíncrono y las fuentes tardan).
 *   · `lienzo`: el `id` del elemento que se fotografía.
 *   · `anchoPx`: el ancho de la tarjeta, el mismo que usa el navegador del operador: el cómodo de una imagen en
 *     WhatsApp.
 */
export const LA_FOTO_DEL_RASTREO = {
  ruta: '/foto/rastreo-shalom',
  pintar: '__pintarLaFoto',
  lienzo: 'la-foto',
  anchoPx: 560,
} as const;

/** Lo que la página recibe para pintar: el rastreo tal cual lo guardó el envío, y la guía y la agencia de respaldo. */
export interface OpcionesDeLaFoto {
  numeroDeGuia?: string | null;
  claveDeAgencia?: string | null;
}
